import { parseDate } from '../helpers/parse-date';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const isBlank = (value) => value === undefined || value === null || String(value).trim() === '';

// A Yes/No toggle: blank is allowed (the import applies the field's default).
// Only "yes"/"no" (case-insensitive) are accepted — the importer keys on
// `=== 'yes'`/`=== 'no'`, so wider truthy spellings like "true"/"1" would pass
// here but be silently ignored on import.
const isYesNo = (value) => isBlank(value) || ['yes', 'no'].includes(value.trim().toLowerCase());

// "manager" | "auto" | a member email address. The importer's parseApproval
// compares the raw value with no trim or case-folding, so we match it exactly:
// "Manager" or " manager " would otherwise pass here and then fall through to
// auto-approval on import, stranding the Manager Email the row was made to supply.
const isApprovalValue = (value) => value === 'manager' || value === 'auto' || EMAIL_REGEX.test(value);

// Manager Email must resolve a manager whenever an approval field routes to one.
// Exact "manager" match mirrors the importer, so the requirement fires on exactly
// the rows the server will actually route to a manager.
const requiresManagerEmail = (row) =>
  [row['Time Off Approval'], row['Expense Approval']].some((value) => value === 'manager');

const members = {
  ID: {
    requirementType: 'required',
    description:
      'A reference value used to identify the member during the import. Assigned to the member\'s "Internal ID" field.',
    dataType: 'string',
    validator: (value) => !isBlank(value),
  },
  Name: {
    requirementType: 'required',
    description: 'Full name of the member.',
    dataType: 'string',
    validator: (value) => !isBlank(value),
  },
  Email: {
    requirementType: 'required',
    description: 'Email address of the member.',
    dataType: 'email',
    validator: (value) => EMAIL_REGEX.test((value || '').trim()),
  },
  Type: {
    requirementType: 'optional',
    description: 'Employment type: "employee", "contractor", or "other". Defaults to "employee" when blank.',
    dataType: 'string',
    validator: (value) => isBlank(value) || ['employee', 'contractor', 'other'].includes(value.trim().toLowerCase()),
  },
  Billable: {
    requirementType: 'required',
    description: 'Whether the member is billable. Set to "Yes" for billable or "No" for non-billable.',
    dataType: 'boolean',
    validator: (value) => !isBlank(value) && ['yes', 'no'].includes(value.trim().toLowerCase()),
  },
  Status: {
    requirementType: 'optional',
    description: 'Set to "Inactive" to block workspace access. Defaults to "Active".',
    dataType: 'string',
    validator: (value) => isBlank(value) || ['active', 'inactive'].includes(value.trim().toLowerCase()),
  },
  'Labor Cost Type': {
    requirementType: 'optional',
    description: 'One of "hourly", "fixed_hourly", or "fixed_monthly". Defaults to "fixed_hourly" when blank.',
    dataType: 'string',
    validator: (value) =>
      isBlank(value) || ['hourly', 'fixed_hourly', 'fixed_monthly'].includes(value.trim().toLowerCase()),
  },
  'Default Bill Rate': {
    requirementType: 'optional',
    description: 'Default rate assigned when the member is added to a project.',
    dataType: 'number',
    validator: (value) => {
      if (isBlank(value)) return true;
      const num = Number(value);
      return Number.isFinite(num) && num >= 0;
    },
  },
  'Default Currency': {
    requirementType: 'optional',
    description:
      'Three-letter ISO currency code for the default bill rate (for example USD, EUR, GBP). Defaults to the workspace currency.',
    dataType: 'string',
    validator: (value) => isBlank(value) || /^[A-Z]{3}$/.test(value.trim().toUpperCase()),
  },
  'Start Date': {
    requirementType: 'optional',
    description:
      'Date the member becomes active, in "YYYY-MM-DD" or "MM/DD/YYYY" format. Defaults to the import date when blank. Also anchors the member\'s auto-generated Capacity, Cost, and Target periods, so an inaccurate date produces inaccurate downstream records.',
    dataType: 'date',
    validator: (value) => isBlank(value) || !!parseDate(value),
  },
  'End Date': {
    requirementType: 'optional',
    description:
      'Date the member ends activity, in "YYYY-MM-DD" or "MM/DD/YYYY" format. Used for reporting only; must be on or after Start Date.',
    dataType: 'date',
    validator: (value, row) => {
      if (isBlank(value)) return true;
      const endDate = parseDate(value);
      if (!endDate) return false;
      const startDate = parseDate(row['Start Date']);
      return !startDate || endDate >= startDate;
    },
  },
  'Security Role': {
    requirementType: 'optional',
    description:
      'Name of an existing security role to assign (for example "Workspace Admin"). Matched exactly; the default role is used when no match is found.',
    dataType: 'string',
    validator: () => true,
  },
  'Job Title': {
    requirementType: 'optional',
    description: 'Job title for the member; the custom data record is created automatically if needed.',
    dataType: 'string',
    validator: () => true,
  },
  Practice: {
    requirementType: 'optional',
    description:
      'Name of an existing Practice to assign. Matched exactly; no practice is assigned when no match is found.',
    dataType: 'string',
    validator: () => true,
  },
  Location: {
    requirementType: 'optional',
    description:
      'Name of an existing Location to assign. Matched exactly; no location is assigned when no match is found.',
    dataType: 'string',
    validator: () => true,
  },
  'Holiday Schedule': {
    requirementType: 'optional',
    description:
      'Name of an existing Holiday Schedule to assign. Matched exactly; no schedule is assigned when no match is found.',
    dataType: 'string',
    validator: () => true,
  },
  Discipline: {
    requirementType: 'optional',
    description:
      'Name of an existing Discipline to assign. Matched exactly; no discipline is assigned when no match is found.',
    dataType: 'string',
    validator: () => true,
  },
  'Manager Email': {
    requirementType: 'conditional',
    description:
      'Email of the member\'s direct manager. Must match another member\'s email in the workspace or in this same import file. Required when Time Off Approval or Expense Approval is "manager".',
    dataType: 'email',
    validator: (value, row) => {
      if (isBlank(value)) return !requiresManagerEmail(row);
      return EMAIL_REGEX.test(value.trim());
    },
  },
  Tags: {
    requirementType: 'optional',
    description:
      'One tag or a semicolon-delimited list of tags. Tags that do not exist in the workspace are created automatically.',
    dataType: 'string',
    validator: () => true,
  },
  Skills: {
    requirementType: 'optional',
    description:
      'One skill or a semicolon-delimited list of skills. Skills that do not exist in the workspace are created automatically.',
    dataType: 'string',
    validator: () => true,
  },
  'Require Capacity': {
    requirementType: 'optional',
    description:
      'Set to "Yes" to require the member to fill capacity before submitting a timesheet. Not required by default.',
    dataType: 'boolean',
    validator: isYesNo,
  },
  'Time Off Tracking': {
    requirementType: 'optional',
    description: 'Set to "No" to disable time off tracking. Enabled by default.',
    dataType: 'boolean',
    validator: isYesNo,
  },
  'Time Off Approval': {
    requirementType: 'optional',
    description:
      'Who approves time off: "manager", "auto", or a member email address. When "manager", the Manager Email field must be provided.',
    dataType: 'string',
    validator: (value) => isBlank(value) || isApprovalValue(value),
  },
  'Expense Approval': {
    requirementType: 'optional',
    description:
      'Who approves internal expenses without a project: "manager", "auto", or a member email address. When "manager", the Manager Email field must be provided. Automatically approved by default.',
    dataType: 'string',
    validator: (value) => isBlank(value) || isApprovalValue(value),
  },
  'Missing Time Reminders': {
    requirementType: 'optional',
    description: 'Set to "No" to disable missing-time reminders. Enabled by default.',
    dataType: 'boolean',
    validator: isYesNo,
  },
  'Timesheet Submission Reminders': {
    requirementType: 'optional',
    description: 'Set to "No" to disable timesheet submission reminders. Enabled by default.',
    dataType: 'boolean',
    validator: isYesNo,
  },
  'Automatic Timesheet Submission Confirmation': {
    requirementType: 'optional',
    description: 'Set to "No" to disable automatic timesheet submission confirmation. Enabled by default.',
    dataType: 'boolean',
    validator: isYesNo,
  },
};

export default members;
